import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchFromPaymentsAPI } from '../services/common.js';
import ClaimSelect, { usePatientClaims } from './ClaimSelect';
import {
    createInvoice,
    listDevices,
    sendInvoiceToDevice,
    createStaffSaveCardSession,
    chargeInvoiceWithSavedCard,
    chargeInvoiceWithNewCard,
    listPatientPaymentMethods,
    logInvoiceActivity,
} from '../services/paymentsService.js';

/**
 * CollectPaymentModal
 * ===================
 *
 * The Dental-Intelligence-style "Collect payment" wizard: start from a
 * patient (not a pre-existing invoice) and, in a couple of clicks, take a
 * payment. Two methods:
 *
 *   - ``physical`` — Physical terminal. Step 2 shows the device picker (and,
 *     for surcharging practices, the credit/debit declaration). The invoice
 *     is created ONLY when staff hits "Send to Terminal", then pushed to the
 *     reader — so abandoning the wizard never leaves an orphan invoice.
 *
 *   - ``virtual``  — Virtual terminal. Step 2 lets the office manager either
 *     key in a NEW card (Rainforest secure form, tokenize/save mode) or, when
 *     the patient has cards on file, pick a SAVED card to charge. Nothing is
 *     created until staff submits:
 *       • New card — on the component's ``approved`` event we create the
 *         invoice and hand the tokenized card to ``charge-new-card``. The
 *         server saves it only when "Save this card" is ticked; otherwise it
 *         charges the card once and never stores it (the card form's session
 *         is minted with ``purpose: 'charge'`` so Rainforest's webhook won't
 *         save it either).
 *       • Saved card — create the invoice then charge the chosen vault id
 *         directly (no re-vaulting; the card is already on file).
 *
 *     "This is an insurance payment" tags the invoice as an internal A/R
 *     record; when checked we force "Save this card" off + disabled. The
 *     server enforces the same rule: a card is never saved behind an
 *     insurance invoice (it's the carrier's card, not the patient's).
 *
 * Either way the created + charged/queued invoice is handed to ``onPresent``
 * so the parent (InvoiceTab) can show its existing "waiting for payment"
 * modal (with sandbox Simulate-Tap etc.) — this component adds no new
 * settlement UI.
 *
 * Props
 *   practiceId  — active practice.
 *   method      — 'physical' | 'virtual'.
 *   settings    — practice payments settings (surchargeEnabled, surchargePctCap).
 *   onCancel()  — dismiss without creating/charging anything.
 *   onPresent({ invoice, deviceId, mode, billingPreview, declaredCardType })
 *               — hand off to the parent's waiting-for-payment modal.
 *   onToast(msg, type) — surface a transient message via the parent's toast.
 *   initialPatient — optional { id, patientId, name, phoneNumber, email }:
 *               start on the details step for this patient (e.g. a family on
 *               the Schedule tab). "Back" still returns to the search.
 *   initialDescription — optional starting description (default 'Payment').
 */

const METHOD_META = {
    physical: {
        label: 'Physical terminal',
        icon: '💳',
        title: 'Physical Terminal Payment',
        blurb: 'Charge a patient by pushing the amount to a card reader for them to tap or insert.',
        cta: 'Send to Terminal',
        defaultSaveCard: true,
    },
    virtual: {
        label: 'Virtual terminal',
        icon: '⌨️',
        title: 'Virtual Terminal Payment',
        blurb: 'Key the patient\'s card into the secure form below to charge them directly.',
        cta: 'Submit',
        defaultSaveCard: true,
    },
};

const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
};

const cardStyle = {
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: '10px',
    maxWidth: '560px',
    width: '92%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.18)',
    // The app is wrapped in `.App { text-align: center }` (CRA default), which
    // block-level text in this modal would otherwise inherit — pin it left.
    textAlign: 'left',
};

const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d6dbd7',
    borderRadius: 6,
    fontSize: 14,
    boxSizing: 'border-box',
};

export default function CollectPaymentModal({
    practiceId,
    method,
    settings,
    onCancel,
    onPresent,
    onToast,
    initialPatient = null,
    initialDescription = '',
}) {
    const meta = METHOD_META[method] || METHOD_META.virtual;
    const surchargeEnabled = !!settings?.surchargeEnabled;
    const surchargePctCap = Number(settings?.surchargePctCap) || 3.0;

    // 'patient' → 'details'
    const [step, setStep] = useState(initialPatient ? 'details' : 'patient');

    // Patient search
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(initialPatient);

    // Amount / details
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState(initialDescription || 'Payment');
    const [isInsurance, setIsInsurance] = useState(false);
    // Optional claim for an insurance payment: lets it post to the PMS as an
    // insurance payment on that claim (when insurance write-back is on).
    const [insuranceClaim, setInsuranceClaim] = useState('');
    const [saveCardOnPay, setSaveCardOnPay] = useState(meta.defaultSaveCard);

    // Physical — devices + declared card type
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState('');
    const [isLoadingDevices, setIsLoadingDevices] = useState(false);
    const [declaredCardType, setDeclaredCardType] = useState(null);

    // Virtual — 'new' keys a fresh card into the Rainforest form; 'saved'
    // charges a card already on file. Defaults to 'new'; the "Card on file"
    // toggle only appears when the patient has saved cards.
    const [cardMode, setCardMode] = useState('new');
    const [savedCards, setSavedCards] = useState([]);
    const [savedCardsLoading, setSavedCardsLoading] = useState(false);
    const [selectedVaultId, setSelectedVaultId] = useState('');

    // Virtual — Rainforest save-only session for the card form
    const [sessionKey, setSessionKey] = useState(null);
    const [pmcId, setPmcId] = useState(null);
    const [cardLoading, setCardLoading] = useState(false);
    // True once Rainforest fires ``loaded`` — the secure iframe is ready and
    // ``component.submit()`` is safe to call. Gates our custom "Pay $X" button.
    const [cardReady, setCardReady] = useState(false);
    const cardRef = useRef(null);

    // Shared submit state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    // Set when create rejects because the patient already has a pending
    // invoice. For virtual we also stash the already-tokenized pm id so the
    // "replace & continue" retry doesn't force staff to re-enter the card.
    const [conflict, setConflict] = useState(null);
    const [pendingPmId, setPendingPmId] = useState(null);

    const amountInputRef = useRef(null);

    const patientId = selectedPatient?.patientId || selectedPatient?.id;
    const patientClaims = usePatientClaims(practiceId, isInsurance ? patientId : null);
    // A claim belongs to one patient: drop it when the patient changes or the
    // payment stops being an insurance one.
    useEffect(() => { setInsuranceClaim(''); }, [patientId, isInsurance]);
    const selectedClaim = patientClaims.claims.find((c) => c.name === insuranceClaim);

    // ---- Patient search ---------------------------------------------------
    const searchPatients = useCallback(async (term) => {
        if (!term.trim()) { setResults([]); return; }
        setIsSearching(true);
        try {
            const data = await fetchFromPaymentsAPI(
                `/api/payments/patients/search?practiceId=${practiceId}&q=${encodeURIComponent(term)}`
            );
            setResults(data?.success ? (data.patients || []) : []);
        } catch (err) {
            console.error('[CollectPayment] patient search failed', err);
            setResults([]);
        } finally {
            setIsSearching(false);
        }
    }, [practiceId]);

    useEffect(() => {
        const t = setTimeout(() => searchPatients(searchTerm), 300);
        return () => clearTimeout(t);
    }, [searchTerm, searchPatients]);

    useEffect(() => {
        if (step === 'details' && amountInputRef.current) amountInputRef.current.focus();
    }, [step]);

    // Insurance payments are an internal A/R record against the carrier — we
    // never vault a card behind one, and never charge a patient's card on
    // file for one. Force "save card" off + disabled and drop back to the
    // new-card form while insurance is checked; restore the method default
    // when it's unchecked.
    useEffect(() => {
        setSaveCardOnPay(isInsurance ? false : meta.defaultSaveCard);
        if (isInsurance) setCardMode('new');
    }, [isInsurance, meta.defaultSaveCard]);

    // ---- Physical: load devices on entering details -----------------------
    useEffect(() => {
        if (step !== 'details' || method !== 'physical') return;
        let cancelled = false;
        (async () => {
            setIsLoadingDevices(true);
            try {
                const res = await listDevices(practiceId, false);
                if (cancelled) return;
                const list = res?.success && Array.isArray(res.devices) ? res.devices : [];
                setDevices(list);
                if (res?.default_device_id) setSelectedDeviceId(res.default_device_id);
                else if (list.length) setSelectedDeviceId(list[0].device_registration_id);
            } catch (err) {
                if (!cancelled) setDevices([]);
            } finally {
                if (!cancelled) setIsLoadingDevices(false);
            }
        })();
        return () => { cancelled = true; };
    }, [step, method, practiceId]);

    // ---- Virtual: load the patient's saved cards on entering details ------
    // Drives the "Card on file" option. Resets the picker whenever the
    // patient changes so a prior patient's card can't leak through.
    useEffect(() => {
        if (step !== 'details' || method !== 'virtual' || !patientId) return;
        let cancelled = false;
        (async () => {
            setSavedCardsLoading(true);
            setCardMode('new');
            setSelectedVaultId('');
            try {
                const result = await listPatientPaymentMethods(practiceId, patientId);
                if (cancelled) return;
                const cards = (result?.success && Array.isArray(result.paymentMethods))
                    ? result.paymentMethods.filter((c) => c.status === 'ACTIVE')
                    : [];
                setSavedCards(cards);
                const def = cards.find((c) => c.isDefault) || cards[0];
                if (def) setSelectedVaultId(def.id);
            } catch (err) {
                if (!cancelled) setSavedCards([]);
            } finally {
                if (!cancelled) setSavedCardsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [step, method, practiceId, patientId]);

    // ---- Virtual: mint the save-only card session for the NEW-card form ---
    // Only needed when keying a fresh card; skip it in "saved card" mode.
    useEffect(() => {
        if (step !== 'details' || method !== 'virtual' || !patientId) return;
        if (cardMode !== 'new') return;
        let cancelled = false;
        (async () => {
            setCardLoading(true);
            setCardReady(false);
            setError(null);
            try {
                const { ok, data } = await createStaffSaveCardSession(practiceId, patientId, { purpose: 'charge' });
                if (cancelled) return;
                if (!ok || !data?.success) {
                    setError(data?.error || 'Could not load the secure card form.');
                    return;
                }
                setSessionKey(data.session_key);
                setPmcId(data.payment_method_config_id);
            } catch (err) {
                if (!cancelled) setError('Could not load the secure card form.');
            } finally {
                if (!cancelled) setCardLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [step, method, practiceId, patientId, cardMode]);

    const parsedAmount = parseFloat(amount);
    const amountValid = Number.isFinite(parsedAmount) && parsedAmount > 0;

    const formatCurrency = (n) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);

    const buildPayload = () => {
        const desc = (description || '').trim() || 'Payment';
        return {
            patientId,
            patientName: selectedPatient.name,
            patientPhone: selectedPatient.phoneNumber || '',
            patientEmail: selectedPatient.email || '',
            description: desc,
            dueDate: '',
            lineItems: [{ description: desc, quantity: 1, price: parsedAmount }],
            total: Math.round(parsedAmount * 100) / 100,
            invoiceType: isInsurance ? 'insurance' : 'copay',
            ...(isInsurance && insuranceClaim ? { claim: insuranceClaim } : {}),
        };
    };

    // createInvoice + conflict routing shared by both methods. Returns the
    // created invoice, or null when it surfaced a conflict / error.
    const createInvoiceOrConflict = async ({ supersede }) => {
        const data = await createInvoice(practiceId, buildPayload(), { supersede });
        if (data && data.success && data.invoice) return data.invoice;
        if (data && data.conflict) {
            setConflict({ existing: data.existing_invoice || {}, count: data.existing_count || 1 });
            return null;
        }
        setError(data?.error || 'Could not create the invoice. Please try again.');
        return null;
    };

    // ---- Physical submit --------------------------------------------------
    const handlePhysicalSend = async ({ supersede = false } = {}) => {
        if (!amountValid) { setError('Enter an amount greater than $0.'); return; }
        if (!selectedDeviceId) { setError('Select a terminal.'); return; }
        if (surchargeEnabled && !declaredCardType) {
            setError('Select Credit or Debit before sending to the terminal.');
            return;
        }
        setIsSubmitting(true);
        setError(null);
        if (!supersede) setConflict(null);
        try {
            const invoice = await createInvoiceOrConflict({ supersede });
            if (!invoice) return;
            const res = await sendInvoiceToDevice(
                practiceId, invoice.id, selectedDeviceId, declaredCardType, saveCardOnPay,
            );
            if (!res?.success) {
                setError(res?.error || 'Could not send to the terminal.');
                return;
            }
            logInvoiceActivity(practiceId, invoice.id, 'sent_to_device', {
                deviceId: selectedDeviceId,
                declaredCardType: res.declaredCardType || declaredCardType || null,
                billingPreview: res.billingPreview || null,
                saveCardOnPay: !!saveCardOnPay,
                source: 'collect_payment',
            });
            onPresent?.({
                invoice,
                deviceId: selectedDeviceId,
                mode: 'payin',
                billingPreview: res.billingPreview || null,
                declaredCardType: res.declaredCardType || declaredCardType || null,
            });
        } catch (err) {
            console.error('[CollectPayment] physical send failed', err);
            setError('Something went wrong sending to the terminal.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // ---- Virtual: card tokenized → create + charge ------------------------
    const chargeTokenizedCard = async (paymentMethodId, { supersede = false } = {}) => {
        if (!amountValid) {
            setError('Enter an amount greater than $0 before submitting the card.');
            return;
        }
        setIsSubmitting(true);
        setError(null);
        if (!supersede) setConflict(null);
        try {
            const invoice = await createInvoiceOrConflict({ supersede });
            if (!invoice) return; // conflict/error already surfaced; pmId retained

            // One call: the server saves the card only when asked (never for
            // insurance), charges it, and logs the charge on the invoice.
            const chargeRes = await chargeInvoiceWithNewCard(practiceId, invoice.id, paymentMethodId, {
                saveCard: saveCardOnPay && !isInsurance,
            });
            if (!chargeRes.ok || !chargeRes.data?.success) {
                const msg = chargeRes.data?.details?.error || chargeRes.data?.error
                    || `Charge failed (HTTP ${chargeRes.status})`;
                setError(msg);
                return;
            }

            onPresent?.({ invoice, deviceId: null, mode: 'payin' });
        } catch (err) {
            console.error('[CollectPayment] virtual charge failed', err);
            setError('Something went wrong charging the card.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // ---- Virtual: charge a card already on file ---------------------------
    // No re-vaulting or transient-card cleanup — the card is already saved, so
    // we just create the invoice and charge the chosen vault id. The "save
    // card" checkbox is irrelevant here (and hidden in the UI).
    const handleSavedCardCharge = async ({ supersede = false } = {}) => {
        if (!amountValid) { setError('Enter an amount greater than $0.'); return; }
        if (!selectedVaultId) { setError('Select a saved card to charge.'); return; }
        setIsSubmitting(true);
        setError(null);
        if (!supersede) setConflict(null);
        try {
            const invoice = await createInvoiceOrConflict({ supersede });
            if (!invoice) return; // conflict/error already surfaced

            const chargeRes = await chargeInvoiceWithSavedCard(practiceId, invoice.id, selectedVaultId);
            if (!chargeRes.ok || !chargeRes.data?.success) {
                const msg = chargeRes.data?.details?.error || chargeRes.data?.error
                    || `Charge failed (HTTP ${chargeRes.status})`;
                setError(msg);
                return;
            }
            logInvoiceActivity(practiceId, invoice.id, 'card_on_file_charged', {
                vaultId: selectedVaultId, source: 'collect_payment_virtual_saved',
            });
            onPresent?.({ invoice, deviceId: null, mode: 'payin' });
        } catch (err) {
            console.error('[CollectPayment] saved-card charge failed', err);
            setError('Something went wrong charging the card.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Wire the Rainforest card component events (virtual only).
    useEffect(() => {
        if (method !== 'virtual' || !sessionKey || !cardRef.current) return undefined;
        const el = cardRef.current;

        const handleApproved = (event) => {
            const raw = event?.detail;
            const env = Array.isArray(raw) ? raw[0] : raw;
            const d = (env && env.data) || env || {};
            const pmId = d.payment_method_id || d.paymentMethodId || d.payment_method?.id
                || d.id || env?.payment_method_id || env?.id;
            if (!pmId) { setError('The card form did not return a payment method.'); return; }
            setPendingPmId(pmId);
            chargeTokenizedCard(pmId);
        };
        const handleDeclined = () => { setIsSubmitting(false); setError('The card was declined. Please try a different card.'); };
        const handleError = () => { setIsSubmitting(false); setError('There was a problem with the card form. Please try again.'); };
        const handleLoaded = () => setCardReady(true);

        el.addEventListener('approved', handleApproved);
        el.addEventListener('declined', handleDeclined);
        el.addEventListener('error', handleError);
        el.addEventListener('loaded', handleLoaded);
        el.addEventListener('payment-method', handleApproved);
        el.addEventListener('payment_method', handleApproved);
        el.addEventListener('tokenized', handleApproved);
        return () => {
            el.removeEventListener('approved', handleApproved);
            el.removeEventListener('declined', handleDeclined);
            el.removeEventListener('error', handleError);
            el.removeEventListener('loaded', handleLoaded);
            el.removeEventListener('payment-method', handleApproved);
            el.removeEventListener('payment_method', handleApproved);
            el.removeEventListener('tokenized', handleApproved);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [method, sessionKey, cardMode, amount, saveCardOnPay, isInsurance, insuranceClaim, description]);

    // Custom "Pay $X" button for the virtual flow. The Rainforest card form
    // runs with ``hide-button`` so its own "Store Details" button is hidden;
    // we drive tokenization via ``component.submit()`` instead, then the
    // ``approved`` handler creates the invoice + charges. Keeps the visible
    // CTA a truthful "Pay $45" rather than the tokenizer's "Store Details".
    const handleCardPay = () => {
        if (!amountValid) { setError('Enter an amount greater than $0.'); return; }
        const comp = cardRef.current;
        if (!comp || typeof comp.submit !== 'function') {
            setError('The card form is still loading. Please try again in a moment.');
            return;
        }
        setError(null);
        setIsSubmitting(true);
        try {
            comp.submit();
        } catch (err) {
            console.error('[CollectPayment] card submit failed', err);
            setIsSubmitting(false);
            setError('Could not submit the card. Please try again.');
        }
    };

    // Surcharge preview for the credit/debit picker + Send button (physical).
    const baseCents = Math.round((parsedAmount || 0) * 100);
    const surchargeCents = Math.floor((baseCents * surchargePctCap) / 100);

    // Local surcharge preview for a saved card. The server applies surcharge
    // at charge time based on the card's cached type; we mirror that rule here
    // (credit adds the cap %, debit/unknown do not) so the picker shows the
    // total. Returns cents, or the base when surcharging is off.
    const savedCardTotalCents = (card) => {
        if (!surchargeEnabled) return baseCents;
        const type = (card?.card?.type || '').toUpperCase();
        return type === 'CREDIT' ? baseCents + surchargeCents : baseCents;
    };

    return (
        <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}>
            <div style={cardStyle}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', color: '#1f5a3d', textTransform: 'uppercase' }}>
                            {meta.icon} {meta.title}
                        </div>
                        {selectedPatient && (
                            <div data-phi style={{ marginTop: 6, fontSize: 18, fontWeight: 700, color: '#1a1d1b' }}>
                                {selectedPatient.name}
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        aria-label="Close"
                        style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, color: '#8a908c', cursor: 'pointer' }}
                    >
                        ×
                    </button>
                </div>

                {step === 'patient' && (
                    <div style={{ marginTop: 14 }}>
                        <div style={{ fontWeight: 600, color: '#2b302d', marginBottom: 10 }}>
                            Find a patient to charge
                        </div>
                        <input
                            type="text"
                            autoFocus
                            placeholder="Search by name, email, or phone…"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={inputStyle}
                        />
                        {searchTerm && (
                            <div style={{
                                border: '1px solid #eef0ee', borderTop: 'none', maxHeight: 240,
                                overflowY: 'auto', backgroundColor: 'white', borderRadius: '0 0 6px 6px',
                            }}>
                                {isSearching ? (
                                    <div style={{ padding: 12, color: '#6b716d', textAlign: 'center', fontSize: 13 }}>Searching…</div>
                                ) : results.length === 0 ? (
                                    <div style={{ padding: 12, color: '#6b716d', textAlign: 'center', fontSize: 13 }}>No patients found</div>
                                ) : (
                                    results.map((p) => (
                                        <div
                                            key={p.id || p.patientId}
                                            onClick={() => {
                                                setSelectedPatient(p);
                                                setSearchTerm('');
                                                setResults([]);
                                                setStep('details');
                                            }}
                                            style={{ padding: 10, borderBottom: '1px solid #eef0ee', cursor: 'pointer' }}
                                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f7f8f7')}
                                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                                        >
                                            <div data-phi style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                                            <div data-phi style={{ fontSize: 12, color: '#6b716d' }}>
                                                {p.phoneNumber} {p.email && `| ${p.email}`} | ID: {p.patientId}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                            <button type="button" onClick={onCancel} style={secondaryBtn}>Cancel</button>
                        </div>
                    </div>
                )}

                {step === 'details' && (
                    <div style={{ marginTop: 14 }}>
                        <div style={{ fontSize: 13, color: '#6b716d', marginBottom: 16 }}>{meta.blurb}</div>

                        {/* Amount */}
                        <label style={labelStyle}>Requested amount</label>
                        <div style={{ position: 'relative', marginBottom: 16 }}>
                            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b716d', fontSize: 15 }}>$</span>
                            <input
                                ref={amountInputRef}
                                type="number"
                                min="0"
                                step="0.01"
                                inputMode="decimal"
                                placeholder="0.00"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && method === 'physical' && amountValid && !isSubmitting) {
                                        handlePhysicalSend();
                                    }
                                }}
                                style={{ ...inputStyle, padding: '10px 12px 10px 26px', fontSize: 15 }}
                            />
                        </div>

                        {/* Description */}
                        <label style={labelStyle}>Description</label>
                        <input
                            type="text"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            style={{ ...inputStyle, marginBottom: 16 }}
                        />

                        {/* Insurance toggle */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, cursor: 'pointer' }}>
                            <input type="checkbox" checked={isInsurance} onChange={(e) => setIsInsurance(e.target.checked)} style={{ cursor: 'pointer' }} />
                            <span style={{ fontSize: 14, color: '#2b302d' }}>This is an insurance payment</span>
                        </label>

                        {/* Optional claim — same picker as the insurance invoice. */}
                        {isInsurance && (
                            <div style={{ marginBottom: 16 }}>
                                <label style={labelStyle}>Claim (optional)</label>
                                <ClaimSelect
                                    ariaLabel="Claim"
                                    claims={patientClaims.claims}
                                    loading={patientClaims.loading}
                                    value={insuranceClaim}
                                    onChange={setInsuranceClaim}
                                    style={{ ...inputStyle, backgroundColor: 'white' }}
                                />
                                {selectedClaim && selectedClaim.estimatedInsurancePayment > 0 && (
                                    <div style={{ fontSize: 12, color: '#6b716d', marginTop: 4 }}>
                                        Insurance estimate {formatCurrency(selectedClaim.estimatedInsurancePayment)}
                                        {Math.round(selectedClaim.estimatedInsurancePayment * 100) !== Math.round((parsedAmount || 0) * 100)
                                            && ' — differs from the amount, so on Open Dental staff post it from the EOB.'}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ---- Physical: device picker + credit/debit ---- */}
                        {method === 'physical' && (
                            <>
                                <label style={labelStyle}>Terminal</label>
                                {isLoadingDevices ? (
                                    <div style={{ padding: 12, color: '#6b716d', fontSize: 13 }}>Loading terminals…</div>
                                ) : devices.length === 0 ? (
                                    <div style={{ padding: 12, backgroundColor: '#fdf3e3', borderRadius: 6, color: '#a8520c', fontSize: 13, marginBottom: 16 }}>
                                        No terminals registered. Add one in the Device Management tab first.
                                    </div>
                                ) : (
                                    <select
                                        value={selectedDeviceId}
                                        onChange={(e) => setSelectedDeviceId(e.target.value)}
                                        style={{ ...inputStyle, marginBottom: 16, backgroundColor: 'white' }}
                                    >
                                        {devices.map((d) => (
                                            <option key={d.device_registration_id} value={d.device_registration_id}>
                                                {d.device_name} ({d.make} {d.model})
                                            </option>
                                        ))}
                                    </select>
                                )}

                                {surchargeEnabled && amountValid && (
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={labelStyle}>Card type</label>
                                        <div style={{ display: 'flex', gap: 10 }}>
                                            {[
                                                { v: 'CREDIT', t: 'Credit Card', s: `Total ${formatCurrency((baseCents + surchargeCents) / 100)} (incl. ${surchargePctCap}% surcharge)` },
                                                { v: 'DEBIT', t: 'Debit Card', s: `Total ${formatCurrency(baseCents / 100)} (no surcharge)` },
                                            ].map(({ v, t, s }) => {
                                                const sel = declaredCardType === v;
                                                return (
                                                    <label key={v} style={{
                                                        display: 'flex', gap: 8, flex: 1, padding: '10px 12px',
                                                        border: `1px solid ${sel ? '#1f5a3d' : '#e3e7e4'}`,
                                                        backgroundColor: sel ? '#e6efe9' : 'white',
                                                        borderRadius: 6, cursor: 'pointer',
                                                    }}>
                                                        <input type="radio" name="declared" checked={sel} onChange={() => setDeclaredCardType(v)} style={{ marginTop: 3 }} />
                                                        <span>
                                                            <span style={{ fontWeight: 700, fontSize: 14, color: '#1a1d1b' }}>{t}</span>
                                                            <span style={{ display: 'block', fontSize: 12, color: '#6b716d' }}>{s}</span>
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#6b716d', marginTop: 8 }}>
                                            Ask the patient "credit or debit?" before tap — required to apply the correct billing rule.
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* ---- Virtual: new card vs. card on file ---- */}
                        {method === 'virtual' && (
                            <div style={{ marginBottom: 16 }}>
                                {/* Mode toggle — only when the patient has cards on file
                                    and this isn't an insurance payment (insurance can't
                                    use a patient's saved card). */}
                                {savedCards.length > 0 && !isInsurance && (
                                    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                                        {[
                                            { v: 'new', t: 'New card' },
                                            { v: 'saved', t: 'Card on file' },
                                        ].map(({ v, t }) => {
                                            const sel = cardMode === v;
                                            return (
                                                <button
                                                    key={v}
                                                    type="button"
                                                    onClick={() => { setCardMode(v); setError(null); }}
                                                    style={{
                                                        flex: 1,
                                                        padding: '8px 12px',
                                                        borderRadius: 6,
                                                        fontSize: 13,
                                                        fontWeight: 600,
                                                        cursor: 'pointer',
                                                        border: `1px solid ${sel ? '#1f5a3d' : '#e3e7e4'}`,
                                                        backgroundColor: sel ? '#e6efe9' : 'white',
                                                        color: sel ? '#13462f' : '#6b716d',
                                                    }}
                                                >
                                                    {t}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {cardMode === 'saved' ? (
                                    <>
                                        <label style={labelStyle}>Card on file</label>
                                        {savedCardsLoading ? (
                                            <div style={{ color: '#6b716d', fontSize: 13 }}>Loading saved cards…</div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                {savedCards.map((m) => {
                                                    const sel = selectedVaultId === m.id;
                                                    const brand = (m.card?.brand || 'Card').replace(/_/g, ' ');
                                                    const last4 = m.card?.last4 ? ` •• ${m.card.last4}` : '';
                                                    const exp = (m.card?.expirationMonth && m.card?.expirationYear)
                                                        ? `Exp ${String(m.card.expirationMonth).padStart(2, '0')}/${String(m.card.expirationYear).slice(-2)}`
                                                        : '';
                                                    const cardType = (m.card?.type || '').toUpperCase();
                                                    const typeLabel = cardType === 'CREDIT' ? 'Credit' : cardType === 'DEBIT' ? 'Debit' : null;
                                                    const total = savedCardTotalCents(m);
                                                    return (
                                                        <label key={m.id} style={{
                                                            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                                                            border: `1px solid ${sel ? '#1f5a3d' : '#d6dbd7'}`,
                                                            backgroundColor: sel ? '#f3f7fb' : 'white',
                                                            borderRadius: 6, cursor: 'pointer',
                                                        }}>
                                                            <input
                                                                type="radio"
                                                                name="collectVaultId"
                                                                checked={sel}
                                                                onChange={() => setSelectedVaultId(m.id)}
                                                            />
                                                            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 14, fontWeight: 600, color: '#1a1d1b' }}>
                                                                    <span>{brand}{last4}{m.isDefault ? ' · Default' : ''}</span>
                                                                    {typeLabel && (
                                                                        <span style={{
                                                                            fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 999,
                                                                            textTransform: 'uppercase', letterSpacing: 0.4,
                                                                            backgroundColor: typeLabel === 'Credit' ? '#fdf3e3' : '#e6f6f3',
                                                                            color: typeLabel === 'Credit' ? '#a8520c' : '#0e7c66',
                                                                            border: `1px solid ${typeLabel === 'Credit' ? '#f3d9ae' : '#b6e5dc'}`,
                                                                        }}>{typeLabel}</span>
                                                                    )}
                                                                </div>
                                                                {(exp || (total != null && total !== baseCents)) && (
                                                                    <div style={{ color: '#8a908c', fontSize: 12, marginTop: 2 }}>
                                                                        {exp}
                                                                        {total != null && total !== baseCents && amountValid && (
                                                                            <> · Total {formatCurrency(total / 100)} (incl. {surchargePctCap}% surcharge)</>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <label style={labelStyle}>Credit card information</label>
                                        {cardLoading ? (
                                            <div style={{ color: '#6b716d', fontSize: 13 }}>Loading secure card form…</div>
                                        ) : sessionKey && pmcId ? (
                                            <rainforest-payment
                                                key={sessionKey}
                                                ref={cardRef}
                                                session-key={sessionKey}
                                                payment-method-config-id={pmcId}
                                                allowed-methods="CARD"
                                                hide-button=""
                                            ></rainforest-payment>
                                        ) : null}
                                    </>
                                )}
                            </div>
                        )}

                        {/* Save card — hidden for "card on file" (already saved). */}
                        {!(method === 'virtual' && cardMode === 'saved') && (
                            <label style={{
                                display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16,
                                cursor: isInsurance ? 'not-allowed' : 'pointer',
                                opacity: isInsurance ? 0.55 : 1,
                            }}>
                                <input
                                    type="checkbox"
                                    checked={saveCardOnPay && !isInsurance}
                                    disabled={isInsurance}
                                    onChange={(e) => setSaveCardOnPay(e.target.checked)}
                                    style={{ marginTop: 3, cursor: isInsurance ? 'not-allowed' : 'pointer' }}
                                />
                                <span>
                                    <span style={{ fontSize: 14, color: '#1a1d1b', fontWeight: 600 }}>Save this card for future payments</span>
                                    <span style={{ display: 'block', fontSize: 12, color: '#6b716d' }}>
                                        {isInsurance
                                            ? 'Insurance payment: the insurance company\u2019s card will not be saved. Insurance cards are never added to a patient\u2019s card on file.'
                                            : 'Securely store the card so the patient can pay future invoices in one click.'}
                                    </span>
                                </span>
                            </label>
                        )}

                        {conflict && (
                            <div style={{ backgroundColor: '#fdf3e3', border: '1px solid #f3d9ae', color: '#a8520c', padding: 12, borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
                                This patient already has {conflict.count > 1 ? `${conflict.count} pending invoices` : 'a pending invoice'}
                                {conflict.existing?.total != null && ` (${formatCurrency(conflict.existing.total)})`}. Replacing will supersede it.
                                <div style={{ marginTop: 10 }}>
                                    <button
                                        type="button"
                                        disabled={isSubmitting}
                                        onClick={() => {
                                            if (method === 'physical') return handlePhysicalSend({ supersede: true });
                                            if (cardMode === 'saved') return handleSavedCardCharge({ supersede: true });
                                            return chargeTokenizedCard(pendingPmId, { supersede: true });
                                        }}
                                        style={{ backgroundColor: '#a8520c', color: 'white', border: 'none', padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                                    >
                                        Replace &amp; continue
                                    </button>
                                </div>
                            </div>
                        )}

                        {isSubmitting && (
                            <div style={{ color: '#6b716d', fontSize: 13, marginBottom: 12 }}>
                                {method === 'virtual' ? 'Processing…' : 'Working…'}
                            </div>
                        )}
                        {error && (
                            <div style={{ color: '#b42318', fontSize: 13, marginBottom: 12 }}>{error}</div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                            <button
                                type="button"
                                onClick={() => { setStep('patient'); setConflict(null); setError(null); }}
                                disabled={isSubmitting}
                                style={secondaryBtn}
                            >
                                Back
                            </button>
                            {method === 'physical' ? (
                                <button
                                    type="button"
                                    onClick={() => handlePhysicalSend()}
                                    disabled={!amountValid || isSubmitting || !selectedDeviceId || devices.length === 0 || (surchargeEnabled && !declaredCardType)}
                                    style={primaryBtn(!amountValid || isSubmitting || !selectedDeviceId || devices.length === 0 || (surchargeEnabled && !declaredCardType))}
                                >
                                    {isSubmitting ? 'Working…' : amountValid ? `${meta.cta} · ${formatCurrency(parsedAmount)}` : meta.cta}
                                </button>
                            ) : cardMode === 'saved' ? (
                                // Virtual — charge the selected card on file.
                                <button
                                    type="button"
                                    onClick={() => handleSavedCardCharge()}
                                    disabled={!amountValid || isSubmitting || !selectedVaultId}
                                    style={primaryBtn(!amountValid || isSubmitting || !selectedVaultId)}
                                >
                                    {isSubmitting ? 'Processing…' : amountValid ? `Pay ${formatCurrency(parsedAmount)}` : 'Pay'}
                                </button>
                            ) : (
                                // Virtual — our own "Pay $X" button drives the hidden Rainforest
                                // tokenizer via component.submit(); the approved handler then
                                // creates the invoice and charges it.
                                <button
                                    type="button"
                                    onClick={handleCardPay}
                                    disabled={!amountValid || isSubmitting || !cardReady || !sessionKey}
                                    style={primaryBtn(!amountValid || isSubmitting || !cardReady || !sessionKey)}
                                >
                                    {isSubmitting ? 'Processing…' : amountValid ? `Pay ${formatCurrency(parsedAmount)}` : 'Pay'}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

const labelStyle = { display: 'block', fontWeight: 600, color: '#2b302d', marginBottom: 6 };
const secondaryBtn = {
    backgroundColor: '#fff', color: '#6b716d', border: '1px solid #cdd3cf',
    padding: '9px 18px', borderRadius: 6, fontSize: 14, cursor: 'pointer',
};
const primaryBtn = (disabled) => ({
    backgroundColor: disabled ? '#c5d8ea' : '#1f5a3d', color: 'white', border: 'none',
    padding: '10px 22px', borderRadius: 6, fontSize: 14, fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
});
